/**
 * Step5WorkPerformed — work narrative + completed-tasks scratched list
 * + estimated/actual hours + Save button.
 *
 * Mechanic types in what was done; SM can review which tasks were
 * actually completed (unchecks revert them to "assigned").
 */
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { CheckCircle2, FileText } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Step5WorkPerformed({
  jobCard, canEdit, canSMReview, isServiceManager, saving,
  workPerformed, setWorkPerformed,
  hoursWorked, setHoursWorked, derivedEstHours,
  actualHoursWorked, setActualHoursWorked, derivedActualHours,
  hasOpenSession,
  handleSaveMechanicWork, onUpdate,
  readyToQuoteBusy, onToggleReadyToQuote,
}) {
  const completedTasks = (jobCard.line_items || []).filter(li => li.completed);

  const handleUncomplete = async (li) => {
    try {
      const updatedLineItems = (jobCard.line_items || []).map(x =>
        x.line_id === li.line_id ? { ...x, completed: false, completed_at: null } : x,
      );
      const res = await fetch(
        `${BACKEND_URL}/api/job-cards/${jobCard.job_card_id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ line_items: updatedLineItems }),
        },
      );
      if (res.ok) {
        toast.success('Task moved back to assigned');
        onUpdate && onUpdate();
      } else {
        toast.error('Could not move task back');
      }
    } catch (_e) { toast.error('Network error'); }
  };

  return (
    <section className="border border-emerald-200 rounded-md" data-testid="jc-step-5-work-performed">
      <header className="flex items-center gap-2 px-3 py-2 border-b border-emerald-200 bg-emerald-50 rounded-t-md">
        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-600 text-white text-xs font-bold">5</span>
        <FileText className="h-4 w-4 text-emerald-700" />
        <h4 className="text-sm font-semibold text-emerald-900">Work Performed</h4>
        <span className="text-[11px] text-emerald-700/80 ml-auto">Completed tasks from Step 2 appear scratched below · enter Actual Hours here</span>
      </header>
      <div className="p-3 space-y-3">
        <Textarea
          id="work"
          placeholder="Describe work performed..."
          value={workPerformed}
          onChange={(e) => setWorkPerformed(e.target.value)}
          disabled={!canEdit}
          rows={3}
          className="text-base touch-manipulation"
        />

        {completedTasks.length > 0 && (
          <div className="border border-emerald-300 rounded p-2 bg-emerald-50/60" data-testid="completed-tasks-list">
            <p className="text-[11px] font-medium text-emerald-800 mb-1.5 flex items-center gap-1">
              <CheckCircle2 className="h-3 w-3" />
              Completed by mechanic — uncheck if not actually done
            </p>
            <ul className="space-y-1">
              {completedTasks.map(li => (
                <li key={li.line_id} className="flex items-start gap-3 text-base py-1.5" data-testid={`completed-task-${li.line_id}`}>
                  <Checkbox
                    checked
                    disabled={!isServiceManager}
                    onCheckedChange={() => handleUncomplete(li)}
                    className="mt-0.5 h-5 w-5 touch-manipulation"
                  />
                  <span className="flex-1 line-through text-emerald-800/80">
                    {li.description}
                    {li.quantity > 1 && <span className="text-emerald-600/70 text-xs"> · qty {li.quantity}</span>}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex flex-wrap items-end gap-4 pt-2 border-t border-emerald-100">
          <div className="w-32">
            <Label htmlFor="hours" className="text-xs">Est. Hours</Label>
            <Input
              id="hours"
              type="number"
              inputMode="decimal"
              step="0.5"
              min="0"
              placeholder={derivedEstHours || '0'}
              value={hoursWorked || derivedEstHours}
              onChange={(e) => setHoursWorked(e.target.value)}
              disabled={!canEdit}
              className="mt-1 h-11 text-base touch-manipulation"
            />
            {derivedEstHours && !hoursWorked && (
              <p className="text-[10px] text-blue-600 mt-1" data-testid="est-hours-auto-tip">
                auto · from task quotes
              </p>
            )}
          </div>
          <div className="w-32">
            <Label htmlFor="actual-hours" className="text-xs flex items-center gap-1">
              Actual Hours <span className="text-red-500">*</span>
            </Label>
            <Input
              id="actual-hours"
              type="number"
              inputMode="decimal"
              step="0.5"
              min="0"
              placeholder={derivedActualHours || '0'}
              value={actualHoursWorked || derivedActualHours}
              onChange={(e) => setActualHoursWorked(e.target.value)}
              disabled={!canEdit && !canSMReview}
              className="mt-1 h-11 text-base touch-manipulation"
            />
            <p className="text-xs text-slate-500 mt-1">
              {derivedActualHours && !actualHoursWorked ? (
                <span className="text-blue-600 inline-flex items-center gap-1" data-testid="actual-hours-auto-tip">
                  auto · from clock log
                  {hasOpenSession && (
                    <span
                      className="inline-flex items-center gap-1 text-emerald-700 ml-1"
                      data-testid="actual-hours-live-pill"
                      title="Live — refreshes every 10s"
                    >
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                      </span>
                      live
                    </span>
                  )}
                </span>
              ) : (
                'Required for review'
              )}
            </p>
          </div>
          {canEdit && (
            <div className="flex flex-wrap items-center gap-2">
              <Button onClick={handleSaveMechanicWork} disabled={saving} className="min-h-11 touch-manipulation" data-testid="jc-save-work-details-btn">
                {saving ? 'Saving...' : 'Save Work Details'}
              </Button>
              {/* iter164 — Ready to Quote button. Mechanic taps this once
                  inspection + findings are logged so the SM sees the
                  orange "READY TO QUOTE" pill on the JC tile and knows
                  to start invoicing. Idempotent toggle. Auto-clears
                  when SM creates an invoice from the JC. */}
              {onToggleReadyToQuote && (
                <Button
                  type="button"
                  onClick={onToggleReadyToQuote}
                  disabled={saving || readyToQuoteBusy || !!jobCard.invoice_id}
                  className={`min-h-11 touch-manipulation ${
                    jobCard.ready_to_quote
                      ? 'bg-orange-100 text-orange-900 border border-orange-400 hover:bg-orange-200'
                      : 'bg-orange-600 text-white hover:bg-orange-700'
                  }`}
                  data-testid="jc-ready-to-quote-btn"
                  title={jobCard.invoice_id
                    ? 'Invoice already created for this JC'
                    : jobCard.ready_to_quote
                      ? 'Click to un-flag Ready to Quote (still working on findings)'
                      : 'Flag this JC as Ready to Quote so the SM can create an invoice'}
                >
                  <CheckCircle2 className="h-4 w-4 mr-1" />
                  {readyToQuoteBusy
                    ? '…'
                    : jobCard.ready_to_quote
                      ? 'Ready to Quote ✓'
                      : 'Mark Ready to Quote'}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
